'use client'

import { useMemo } from 'react'
import {
  GoogleMapsPrediction,
  GoogleMapsStructuredPlace
} from '../interfaces/googleMapsInterfaces'
import { useAutocomplete } from '../hooks/useAutocomplete'
import { MdOutlineSearch } from 'react-icons/md'
import { EAmplitudeEvent } from '../interfaces/amplitude-events'
import { useAutocompleteStore } from '../store'
import { SuggestionOriginEnum } from '../interfaces/autocomplete'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider/AmplitudeProvider'
import { composeClasses } from '@wiggot/utils/src/strings'

interface Props {
  /**
   * This is the index of the match.
   */
  index: number

  /**
   * This is the object that contains the data of the match.
   * @type {GoogleMapsPrediction}
   */
  prediction: GoogleMapsPrediction

  /**
   * This function is called when the match is selected.
   * @type {(place: GoogleMapsStructuredPlace) => void}
   */
  onSelected: (place: GoogleMapsStructuredPlace) => void

  /**
   * This is the index of the selected match.
   * @type {number}
   */
  selectedIndex: number

  /**
   * This is the className for an item
   * @type {string}
   */
  className?: string
  itemTextClassName?: string
  searchText?: string
  withRemarkSelected?: boolean
  remarkSelectedClassNames?: string
  focusOptionClassNames?: string
}

/**
 * This component is responsible for displaying the matche of the search.
 * @param index - This is the index of the match.
 * @param prediction - This is the object that contains the data of the match.
 * @param onSelected - This function is called when the match is selected.
 * @param selectedIndex - This is the index of the selected match.
 * @param className - This is the className.
 * @param withRemarkSelected - boolean to remark a selected option with the searchText.
 * @returns {JSX.Element}
 * */
const CoincidenceOption = ({
  index,
  className,
  prediction,
  onSelected,
  selectedIndex,
  searchText,
  withRemarkSelected,
  remarkSelectedClassNames,
  focusOptionClassNames,
  itemTextClassName
}: Props): JSX.Element => {
  const { selectPlace } = useAutocomplete()
  const {
    totalSuggestionsZones,
    totalSuggestionsGoogle,
    setSelectedSuggestionOrigin
  } = useAutocompleteStore()

  const composition = useMemo(() => {
    if (!prediction?.placePrediction?.text?.text) {
      return {
        street: '',
        neighborhood: '',
        concatenatedRest: ''
      }
    }

    const { text } = prediction.placePrediction

    const split = text?.text.split(',')

    const street = split[0]
    const neighborhood = split[1]
    const concatenatedRest = [...split].slice(2).join(', ')

    return {
      street,
      neighborhood,
      concatenatedRest
    }
  }, [prediction?.placePrediction])

  /**
   * This function is called when the match is selected
   * @returns {Promise<void>}
   */
  const select = async (): Promise<void> => {
    const place = await selectPlace({ prediction })

    if (!place) {
      return
    }

    const combinedSuggestions = [
      ...totalSuggestionsZones,
      ...totalSuggestionsGoogle
    ]

    setSelectedSuggestionOrigin(SuggestionOriginEnum.GOOGLE)

    onSelected(place)
  }

  const isSelected = useMemo(() => {
    const textInBox = searchText?.replaceAll(' ', '').toLowerCase()
    const formattedTextInList = `${composition.street},${composition.neighborhood},${composition.concatenatedRest}`
    const textInList = formattedTextInList.replaceAll(' ', '').toLowerCase()
    const textInListWithoutCommas = formattedTextInList
      .replaceAll(' ', '')
      .replaceAll(',', '')
      .toLowerCase()

    return textInBox === textInList || textInBox === textInListWithoutCommas
  }, [
    searchText,
    composition.street,
    composition.neighborhood,
    composition.concatenatedRest
  ])
  return (
    <button
      type="button"
      onClick={select}
      className={[
        'w-full flex justify-start text-left items-center gap-x-[10px] py-[5px] hover:bg-gray-100 text-text-primary',
        index === selectedIndex
          ? composeClasses(
              focusOptionClassNames ? focusOptionClassNames : 'bg-gray-100'
            )
          : '',
        isSelected && withRemarkSelected
          ? composeClasses(
              remarkSelectedClassNames
                ? remarkSelectedClassNames
                : '!text-button-primary !bg-bg-blue-02'
            )
          : '',
        className || ''
      ].join(' ')}
    >
      <MdOutlineSearch className="w-[18px] h-[18px]  !flex justify-center items-center flex-grow-0 flex-shrink-0 basis-auto" />

      <p className={`text-xs truncate ${itemTextClassName}`}>
        {composition.street},{' '}
        <span className="font-bold">{composition.neighborhood}</span>,{' '}
        {composition.concatenatedRest}
      </p>
    </button>
  )
}

export default CoincidenceOption
